import React, { useState, useEffect } from 'react';
import './DirectorProfile.css';

const DirectorProfile = () => {
  const [playingVideos, setPlayingVideos] = useState({});

  // Function to toggle video playback
  const toggleVideo = (videoId) => {
    setPlayingVideos(prev => ({
      ...prev,
      [videoId]: !prev[videoId]
    }));
  };

  return (
    <div className="director-profile">
      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="nav-container">
          <h1 className="nav-title">Stephen Pallam</h1>
        </div>
      </nav>
      
      {/* Deployment version indicator */}
      <div style={{position: 'fixed', bottom: '5px', right: '5px', fontSize: '9px', color: '#999', zIndex: 9999, background: 'rgba(255,255,255,0.8)', padding: '2px 5px', borderRadius: '3px'}}>
        Build: 2026-06-01-23:54
      </div>

      {/* Hero Section with Background */}
      <section className="hero-section-compact">
        <div className="hero-overlay"></div>
        <div className="hero-content-compact">
          <h2 className="hero-title-compact">Film Director & VFX Supervisor</h2>
          <p className="hero-subtitle">
            Debutant NRI filmmaker from USA who created <span className="highlight">Indrani - India's First Female Superhero Film</span> under his production house Shray Motion Pictures
          </p>
          <div className="role-banner">
            Writer • Director • Producer • VFX Supervisor
          </div>
          <div className="achievement-badges-compact">
            <div className="badge-compact">
              <span className="badge-number-compact">1600</span>
              <span className="badge-label-compact">VFX Shots</span>
            </div>
            <div className="badge-compact">
              <span className="badge-number-compact">2h 33m</span>
              <span className="badge-label-compact">Duration</span>
            </div>
            <div className="badge-compact">
              <span className="badge-number-compact">31</span>
              <span className="badge-label-compact">Shooting Days</span>
            </div>
            <div className="badge-compact">
              <span className="badge-number-compact">₹5.5Cr</span>
              <span className="badge-label-compact">Budget</span>
            </div>
          </div>
        </div>
      </section>

      {/* Plot and Videos Section */}
      <section className="plot-videos-section">
        <div className="container">
          <div className="plot-container">
            <h2 className="plot-title">Indrani - The Story</h2>
            <p className="plot-text">
              In 2122 AD, when an enemy country conducts surgical strikes on India, Indrani, working for the 
              Indian Super Force (ISF), must travel back in time to the past to prevent further surgical strikes 
              and save the future of her nation. A thrilling sci-fi superhero adventure that combines time travel, 
              patriotism, and cutting-edge visual effects.
            </p>
          </div>
          
          <div className="videos-row">
            <div className="video-column">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('making')}
              >
                {!playingVideos.making ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/mtyIiLkvOVE/maxresdefault.jpg`}
                      alt="Making of Indrani"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/mtyIiLkvOVE/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/mtyIiLkvOVE?autoplay=1"
                    title="Making of Indrani"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="video-column">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('trailer')}
              >
                {!playingVideos.trailer ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/GWVWCl1GUkE/hqdefault.jpg`}
                      alt="Indrani Trailer"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/GWVWCl1GUkE/mqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/GWVWCl1GUkE?autoplay=1"
                    title="Indrani Trailer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Director Summary Section */}
      <section className="director-summary-section">
        <div className="container">
          <h2 className="section-title">About The Director</h2>
          <div className="director-summary-content">
            <div className="director-summary-text">
              <p>
                <strong>Stephen Pallam</strong> is a filmmaker, VFX supervisor, and storyteller who made history by bringing India's first female superhero to the screen. 
                An NRI from the USA, he blends technical innovation with compelling storytelling to create cinematic experiences that resonate across cultures.
              </p>
            <p>
              For his debut feature film, <strong>Indrani (2024)</strong>, Stephen served as writer, director, producer, and VFX supervisor. 
              In an ambitious attempt rarely seen in Indian cinema, he delivered a 2-hour 33-minute action epic with nearly 1,600 VFX shots, 
              8 major action sequences, and 5 songs—a scale typically requiring budgets 10x larger. Through meticulous planning and innovative 
              execution, the entire production was completed in just 31 shooting days within a remarkably controlled budget of ₹5.5 crore, 
              proving that good quality cinema can be achieved through vision and resourcefulness rather than just financial muscle.
            </p>
              <p>
                With Indrani, Stephen demonstrated his ability to deliver large-scale, technically demanding cinema with both creative vision 
                and commercial appeal. He is currently developing his next ambitious project with a significantly higher budget—a Pan-India 
                adventure epic inspired by the spirit of Indiana Jones, blending thrilling action with the rich tapestry of Indian mythology 
                to captivate audiences across the nation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Characters of Indrani Section */}
      <section className="artists-section">
        <div className="container">
          <h2 className="section-title">Characters of Indrani</h2>
          <div className="artists-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => (
              <div key={index} className="artist-card">
                <img 
                  src={`/images/artists/artist-${index}.png`}
                  alt={`Artist ${index}`}
                  className="artist-image"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x400/1a1a2e/eee?text=Artist+${index}`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Release Promos Section */}
      <section className="promos-section">
        <div className="container">
          <h2 className="section-title">Indrani - Release Promos</h2>
          <div className="promos-grid">
            {/* YouTube Shorts with custom thumbnails - First */}
            <div className="promo-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('promo0')}
              >
                {!playingVideos.promo0 ? (
                  <>
                    <img 
                      src="/images/promos/mogambo-thumb.png"
                      alt="Mogambo Promo"
                      className="video-thumbnail"
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/npFQkC06X18?autoplay=1"
                    title="Mogambo Promo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            
            <div className="promo-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('promo1')}
              >
                {!playingVideos.promo1 ? (
                  <>
                    <img 
                      src="/images/promos/saptagiri-thumb.png"
                      alt="Saptagiri Promo"
                      className="video-thumbnail"
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/ozeI1SRqUho?autoplay=1"
                    title="Saptagiri Promo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>

            {/* Regular YouTube promos */}
            {[
              'orygzv_JbQc',
              'egNPFxz3o84',
              'oqMJO2tYrHo',
              'CLdPOZEC7OE',
              'fvbh05L-ec4',
              'LCRu2KFsMkw'
            ].map((videoId, index) => (
              <div key={index + 2} className="promo-card">
                <div 
                  className="video-thumbnail-wrapper"
                  onClick={() => toggleVideo(`promo${index + 2}`)}
                >
                  {!playingVideos[`promo${index + 2}`] ? (
                    <>
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={`Release Promo ${index + 3}`}
                        className="video-thumbnail"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }}
                      />
                      <div className="play-button-overlay">
                        <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="currentColor"/>
                        </svg>
                      </div>
                    </>
                  ) : (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                      title={`Release Promo ${index + 3}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="video-iframe"
                    ></iframe>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Songs Section */}
      <section className="songs-section">
        <div className="container">
          <h2 className="section-title">Video Songs</h2>
          <div className="songs-grid">
            <div className="song-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('song1')}
              >
                {!playingVideos.song1 ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/ME-afEca6Cc/maxresdefault.jpg`}
                      alt="Gamyam"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/ME-afEca6Cc/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/ME-afEca6Cc?autoplay=1"
                    title="Gamyam"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="song-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('song2')}
              >
                {!playingVideos.song2 ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/lkpSb7d8EWk/maxresdefault.jpg`}
                      alt="Ravanan"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/lkpSb7d8EWk/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/lkpSb7d8EWk?autoplay=1"
                    title="Ravanan"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="song-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('song3')}
              >
                {!playingVideos.song3 ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/JnkdJ4SlAKU/maxresdefault.jpg`}
                      alt="Mogambo"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/JnkdJ4SlAKU/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/JnkdJ4SlAKU?autoplay=1"
                    title="Mogambo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="song-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('song4')}
              >
                {!playingVideos.song4 ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/3R3_IcwEA1k/maxresdefault.jpg`}
                      alt="Halloween"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/3R3_IcwEA1k/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/3R3_IcwEA1k?autoplay=1"
                    title="Halloween"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="song-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('song5')}
              >
                {!playingVideos.song5 ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/iAFL2DZNgh0/maxresdefault.jpg`}
                      alt="Party Party"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/iAFL2DZNgh0/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/iAFL2DZNgh0?autoplay=1"
                    title="Party Party"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interviews Section */}
      <section className="interviews-section">
        <div className="container">
          <h2 className="section-title">Interviews</h2>
          <div className="interviews-grid">
            {[
              'AuH1j3YMH3M',
              'B-OZw_TSk8E',
              'c8oRa8MEYTA',
              'bJv3GB_W18U',
              'qY6lqTWptLY',
              'kLsTbkUObZE',
              'RvnUHeYXHjU',
              'KnP7I50ybPU',
              's5NFCs-5wVU',
              'P-g1qrmiz2c',
              'VN_uVm7E2Yg',
              'oGvfcghnP8I',
              'gaWg-0RVTfQ',
              'xu17_CqbROE',
              'AZWyFJB7VSs',
              '5ZtULG95wXE',
              'JpzhvEKUkvk',
              'VeXPjUmusRo'
            ].map((videoId, index) => (
              <div key={index} className="interview-card">
                <div 
                  className="video-thumbnail-wrapper"
                  onClick={() => toggleVideo(`interview${index}`)}
                >
                  {!playingVideos[`interview${index}`] ? (
                    <>
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={`Interview ${index + 1}`}
                        className="video-thumbnail"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }}
                      />
                      <div className="play-button-overlay">
                        <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="currentColor"/>
                        </svg>
                      </div>
                    </>
                  ) : (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                      title={`Interview ${index + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="video-iframe"
                    ></iframe>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Movies Section - Moved after Video Songs */}
      <section className="movies-section">
        <div className="container">
          <h2 className="section-title">Watch Indrani - Full Movie</h2>
          <div className="movies-grid">
            <div className="movie-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('hindi')}
              >
                {!playingVideos.hindi ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/r1ajWd1C7cI/maxresdefault.jpg`}
                      alt="Indrani - Hindi Version"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/r1ajWd1C7cI/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/r1ajWd1C7cI?autoplay=1"
                    title="Indrani - Hindi Version"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
            <div className="movie-card">
              <div 
                className="video-thumbnail-wrapper"
                onClick={() => toggleVideo('telugu')}
              >
                {!playingVideos.telugu ? (
                  <>
                    <img 
                      src={`https://img.youtube.com/vi/I7gmn1yDXJs/maxresdefault.jpg`}
                      alt="Indrani - Telugu Version"
                      className="video-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/I7gmn1yDXJs/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-button-overlay">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5v14l11-7z" fill="currentColor"/>
                      </svg>
                    </div>
                  </>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/I7gmn1yDXJs?autoplay=1"
                    title="Indrani - Telugu Version"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="video-iframe"
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Contact</h2>
          <div className="contact-content">
            <div className="contact-item">
              <span className="contact-label">Name:</span>
              <span className="contact-value">Stephen Pallam</span>
            </div>
            <div className="contact-item">
              <span className="contact-label">Contact Email:</span>
              <div className="contact-value-multi">
                <a href="mailto:shraymotionpictures@gmail.com">shraymotionpictures@gmail.com</a>
                <a href="mailto:stephenpallam@gmail.com">stephenpallam@gmail.com</a>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-label">Contact Phone:</span>
              <div className="contact-value-multi">
                <span>USA: <a href="tel:+12022907262">+1 202-290-7262</a></span>
                <span>India: <a href="tel:+918309890255">+91 830-989-0255</a></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DirectorProfile;